/**
 * JWT Token utilities for expiration checking and validation
 */

export interface TokenPayload {
  sub: string;
  exp: number;
  type: string;
  [key: string]: any;
}

/**
 * Check if a JWT token is expired
 * @param token - The JWT token to check
 * @returns true if expired, false if valid
 */
export function isTokenExpired(token: string): boolean {
  try {
    const payload = parseTokenPayload(token);
    if (!payload || !payload.exp) {
      return true; // Invalid token or no expiration
    }
    
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    return payload.exp < now;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Treat parsing errors as expired
  }
}

/**
 * Parse JWT token payload without verification
 * @param token - The JWT token to parse
 * @returns The decoded payload or null if invalid
 */
export function parseTokenPayload(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    // Convert base64url to base64 and add padding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = base64.length % 4;
    const padded = padLen ? base64 + '='.repeat(4 - padLen) : base64;
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error parsing token payload:', error);
    return null;
  }
}

/**
 * Get token expiration time as a Date object
 * @param token - The JWT token
 * @returns Date object of expiration or null if invalid
 */
export function getTokenExpirationDate(token: string): Date | null {
  try {
    const payload = parseTokenPayload(token);
    if (!payload || !payload.exp) {
      return null;
    }
    
    return new Date(payload.exp * 1000); // Convert from seconds to milliseconds
  } catch (error) {
    console.error('Error getting token expiration date:', error);
    return null;
  }
}

/**
 * Get time until token expires in seconds
 * @param token - The JWT token
 * @returns Seconds until expiration (negative if expired)
 */
export function getTimeUntilExpiration(token: string): number {
  try {
    const payload = parseTokenPayload(token);
    if (!payload || !payload.exp) {
      return -1; // Invalid token
    }
    
    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now;
  } catch (error) {
    console.error('Error getting time until expiration:', error);
    return -1;
  }
}

/**
 * Check if token needs refresh (expires within 5 minutes)
 * @param token - The JWT token
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string): boolean {
  const timeUntilExpiration = getTimeUntilExpiration(token);
  return timeUntilExpiration > 0 && timeUntilExpiration < 300; // 5 minutes
}
